import {HttpClient, HttpParams, HttpRequest, HttpResponse} from '@angular/common/http';
import {ServerException} from './serverException';
import {ConflictException} from './conflictException';
import {NotFoundException} from './notFoundException';
import {UnauthorizedRequestException} from './unauthorizedRequestException';
import {RequestException} from './requestException';
import {AppService} from '../app/app.service';
import {ProblemDetails} from '../../data/problemDetails';

export class GenericHttpService {

  toFetchRequestsDict: Record<string, Promise<any>> = {};
  fetchedRequestsDict: Record<string, any> = {};
  activeRequestsDict: Record<string, boolean> = {};
  requestTimeDict: Record<string, Date> = {};

  requestsSent: number = 0;
  requestsCached: number = 0;

  get baseUrl() {
    return this.apiUrl;
  }

  constructor(private appService: AppService,
              private httpClient: HttpClient,
              private apiUrl: string) {
  }

  processStatusCode<T>(response: HttpResponse<T>) {
    switch (response.status) {
      case 200:
        return response.body;
      case 201:
        return response.body;
      case 400:
        throw new RequestException(response.status, response.statusText);
      case 401:
        this.appService.showErrorMessage('There seems to be an authentication problem. Please try logging in again.');
        throw new UnauthorizedRequestException(response.status, response.statusText);
      case 404:
        this.appService.showErrorMessage('The record you\t trying to access no longer exists. Please refresh.');
        throw new NotFoundException(response.status, response.statusText);
      case 409:
        this.appService.showWarnMessage('There is a possible conflict. Please solve the merge request.',);
        throw new ConflictException(response.status, response.statusText, response.body);
      case 500:
        this.appService.showErrorMessage('There is a problem with the server. Please contact an admin.');
        throw new ServerException(response.status, response.statusText);
      case 501:
        this.appService.showErrorMessage('This feature has not been added yet.');
        throw new ServerException(response.status, response.statusText);
    }
    return undefined;
  }

  async getWithParams<T>(url: string, queryParams: any) {
    const queryJson = JSON.stringify(queryParams);
    const urlKey = `${url}?${queryJson}`;
    const now = new Date();
    try {
      if (Object.keys(this.requestTimeDict).includes(urlKey)) {
        const requestDate = this.requestTimeDict[urlKey];
        if (requestDate.getTime() > now.getTime() - 1000) {
          this.requestsCached++;
          if (this.activeRequestsDict[urlKey]) {
            const activeRequestResponse = await this.toFetchRequestsDict[urlKey] as HttpResponse<T>;
            return this.processStatusCode(activeRequestResponse);
          } else {
            const fetchedRequestResponse = this.fetchedRequestsDict[urlKey] as HttpResponse<T>;
            return this.processStatusCode(fetchedRequestResponse);
          }
        }
      }
      const request = this.httpClient.get<T>(`${this.apiUrl}${url}`, {
        observe: 'response',
        params: new HttpParams({fromObject: queryParams})}).toPromise();
      this.requestsSent++;
      this.requestTimeDict[urlKey] = new Date();
      this.activeRequestsDict[urlKey] = true;
      this.toFetchRequestsDict[urlKey] = request;
      const response = await request;
      this.activeRequestsDict[urlKey] = false;
      this.fetchedRequestsDict[urlKey] = response;
      return this.processStatusCode(response);
    } catch (e) {
      // tslint:disable-next-line:no-console
      console.trace();
      throw e;
    }
  }

  async get<T>(url: string) {
    const urlKey = `${url}`;
    const now = new Date();
    if (Object.keys(this.requestTimeDict).includes(urlKey)) {
      const requestDate = this.requestTimeDict[urlKey];
      if (requestDate.getTime() > now.getTime() - 1000) {
        this.requestsCached++;
        if (this.activeRequestsDict[urlKey]) {
          const activeRequestResponse = await this.toFetchRequestsDict[urlKey] as HttpResponse<T>;
          return this.processStatusCode(activeRequestResponse);
        } else {
          const fetchedRequestResponse = this.fetchedRequestsDict[urlKey] as HttpResponse<T>;
          return this.processStatusCode(fetchedRequestResponse);
        }
      }
    }
    const request = this.httpClient.get<T>(`${this.apiUrl}${url}`, {
      observe: 'response'
    }).toPromise();
    this.requestsSent++;
    this.requestTimeDict[urlKey] = new Date();
    this.activeRequestsDict[urlKey] = true;
    this.toFetchRequestsDict[urlKey] = request;
    const response = await request;
    this.activeRequestsDict[urlKey] = false;
    this.fetchedRequestsDict[urlKey] = response;
    return this.processStatusCode(response);
  }

  async post<T>(url: string, body: any): Promise<T> {
    try {
      const request = this.httpClient.post<T>(`${this.apiUrl}${url}`, body, {
        observe: 'response'
      }).toPromise();
      const response = await request;
      return this.processStatusCode(response);
    } catch (e) {
      console.error(`Failed POST at: ${url}`);
      // tslint:disable-next-line:no-console
      console.trace();
      throw e;
    }
  }

  async postWithPD<TRequest, TResponse>(url: string, body: any): Promise<TResponse> {
    try {
      const request = this.httpClient.post<TResponse>(`${this.apiUrl}${url}`, body, {
        observe: 'response'
      }).toPromise();
      const response = await request;
      return this.processStatusCode(response);
    } catch (e) {
      if (e.status === 400) {
        throw new ProblemDetails<TRequest>(e.error);
      }
      throw e;
    }
  }

  postFile(url: string, data: any) {
    try {
      const blob = new Blob([data], {
        type: 'text/plain'
      });
      const formData = new FormData();
      formData.append('uploadfile', blob, 'har');
      const params = new HttpParams();
      const options = {
        params,
        reportProgress: true,
      };
      const req = new HttpRequest('POST', `${this.apiUrl}${url}`, formData, options);
      const response = this.httpClient.request(req).toPromise();
      return response;
    } catch (e) {
      console.error(`Failed POST at: ${url}`);
      throw e;
    }
  }

  postFileWithData(url: string, file: any, data: any) {
    try {
      const blob = new Blob([file], {
        type: 'text/plain'
      });
      const blob2 = new Blob([data], {
        type: 'text/plain'
      });
      const formData = new FormData();
      formData.append('uploadfile', blob, 'har');
      formData.append('data', blob2, 'data');
      const params = new HttpParams();
      const options = {
        params,
        reportProgress: true,
      };
      const req = new HttpRequest('POST', `${this.apiUrl}${url}`, formData, options);
      const response = this.httpClient.request(req).toPromise();
      return response;
    } catch (e) {
      console.error(`Failed POST at: ${url}`);
      throw e;
    }
  }

  async delete(url: string, id: string | number) {
    try {
      const response = this.httpClient.delete(`${this.apiUrl}${url}` + '/' + id.toString(), {
        observe: 'response'
      }).toPromise();
      return this.processStatusCode(await response);
    } catch (e) {
      console.error(`Failed DELETE at: ${url}`);
      throw e;
    }
  }

  async put<T>(url: string, id: string | number, body: any) {
    try {
      const request = this.httpClient.put<T>(`${this.apiUrl}${url}` + '/' + id.toString(), body, {
        observe: 'response'
      }).toPromise();
      const response = await request;
      return this.processStatusCode(response);
    } catch (e) {
      console.error(`Failed PUT at: ${url}`);
      throw e;
    }
  }

  async patch(url: string, id: string | number, body: any) {
    try {
      const response = this.httpClient.patch(`${this.apiUrl}${url}` + '/' + id.toString(), body, {
        observe: 'response'
      }).toPromise();
      return this.processStatusCode(await response);
    } catch (e) {
      console.error(`Failed PATCH at: ${url}`);
      // tslint:disable-next-line:no-console
      console.trace();
      throw e;
    }
  }

  async patchWithCustomURL(url: string, body: any) {
    try {
      const response = this.httpClient.patch(`${this.apiUrl}${url}`, body, {
        observe: 'response'
      }).toPromise();
      return this.processStatusCode(await response);
    } catch (e) {
      console.error(`Failed PATCH at: ${url}`);
      // tslint:disable-next-line:no-console
      console.trace();
      throw e;
    }
  }
}
